// src/components/dev/EmulatorSignIn.tsx
import React, { useState } from 'react';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { auth, usingEmulators } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import '../../styles/components/dev/EmulatorSignIn.css';

/**
 * One-click sign-in as a synthetic player, shown only when the browser is
 * pointed at the local emulator stack (VITE_USE_EMULATORS=1).
 *
 * The accounts and their shared password come from tools/dev/seed-dev.mjs —
 * see DEVELOPMENT.md. They exist only inside the Auth emulator, which never
 * holds a real account, so there is nothing here worth protecting. The
 * component is unreachable in production builds: `usingEmulators` is false
 * outside `vite dev`, and the whole module is tree-shaken away.
 */
const SEED_PASSWORD = 'devpassword';

// Index → role, per the seed: player0 is the super admin, every seventh player is VIP, and
// every one has a character on beta, the only world.
const SEED_PLAYERS: Array<{ index: number; note: string }> = [
    { index: 0, note: 'admin · vip' },
    { index: 2, note: 'tavaline' },
    { index: 4, note: 'tavaline' },
    { index: 14, note: 'vip' },
    { index: 1, note: 'tavaline' },
];

export const EmulatorSignIn: React.FC = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [busy, setBusy] = useState<number | null>(null);
    const [error, setError] = useState('');

    if (!usingEmulators) return null;

    const signInAs = async (index: number) => {
        setBusy(index);
        setError('');
        try {
            if (currentUser) await signOut(auth);
            await signInWithEmailAndPassword(auth, `player${index}@dev.local`, SEED_PASSWORD);
            navigate('/dashboard');
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setBusy(null);
        }
    };

    return (
        <aside className="emu" aria-label="Emulaatori sisselogimine">
            <div className="emu__head">
                <span className="emu__dot" />
                Emulaator
            </div>
            <div className="emu__row">
                {SEED_PLAYERS.map(({ index, note }) => (
                    <button
                        key={index}
                        type="button"
                        className="emu__btn"
                        disabled={busy !== null}
                        onClick={() => signInAs(index)}
                        title={`player${index}@dev.local`}
                    >
                        P{index}
                        <span className="emu__note">{note}</span>
                    </button>
                ))}
                {currentUser && (
                    <button
                        type="button"
                        className="emu__btn emu__btn--out"
                        disabled={busy !== null}
                        onClick={() => signOut(auth).then(() => navigate('/'))}
                    >
                        Välja
                    </button>
                )}
            </div>
            {error && <p className="emu__error">{error}</p>}
        </aside>
    );
};
